<template>
  <v-simple-table class="snap-entries-table" dense>
    <thead>
      <tr>
        <th class="snap-entry-date-col">Date</th>
        <th class="snap-entry-hours-col">Hours</th>
        <th>Notes</th>
        <th v-if="showReassignColumn" class="snap-entry-project-col">
          Reassign to
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(entry, idx) in entries" :key="entryKey(entry, idx)">
        <td>{{ entry.dateLabel || "—" }}</td>
        <td class="snap-entry-hours-cell">
          <div class="d-flex flex-wrap align-center" style="gap: 6px">
            <v-text-field
              v-if="isEditingHours(entry)"
              dense
              hide-details
              outlined
              autofocus
              class="snap-hours-input"
              :disabled="Boolean(hoursSavingId)"
              :value="hoursEditValue"
              @input="$emit('update:hoursEditValue', $event)"
              @blur="$emit('commit-hours-edit', entry)"
              @keyup.enter="$emit('commit-hours-edit', entry)"
              @keyup.esc="$emit('cancel-hours-edit')"
              @click.stop
            />
            <span
              v-else
              :class="{
                'snap-hours-editable': canEditHours(entry),
              }"
              :title="
                canEditHours(entry) ? 'Double-click to edit hours' : undefined
              "
              @dblclick.stop="onHoursDblClick(entry)"
            >
              {{ entry.hours || "—" }}
            </span>
            <v-chip
              v-if="entry.editedByLabel && !entry.editedBySelf"
              x-small
              color="blue-grey lighten-4"
              class="snap-edited-by-chip"
              :title="editedByTitle(entry)"
            >
              edited by {{ entry.editedByLabel }}
            </v-chip>
          </div>
        </td>
        <td class="snap-entry-notes">{{ entry.notes || "—" }}</td>
        <td v-if="showReassignColumn" class="snap-entry-project-col">
          <v-select
            v-if="canReassignEntry(entry)"
            dense
            hide-details
            outlined
            :items="projectOptions"
            item-text="name"
            item-value="id"
            :value="entry.projectId"
            :loading="reassignSavingId === entry.timesheetId"
            :disabled="Boolean(reassignSavingId || hoursSavingId)"
            placeholder="Project"
            class="snap-reassign-select"
            @change="onProjectChange(entry, $event)"
          />
          <span v-else class="caption grey--text">—</span>
        </td>
      </tr>
    </tbody>
  </v-simple-table>
</template>

<script>
export default {
  name: "KonekoATimesheetEntriesTable",
  props: {
    entries: {type: Array, default: () => []},
    projectOptions: {type: Array, default: () => []},
    reassignSavingId: {type: [Number, String], default: null},
    hoursSavingId: {type: [Number, String], default: null},
    canReassignEntry: {type: Function, default: () => false},
    canEditHours: {type: Function, default: () => false},
    isEditingHours: {type: Function, default: () => false},
    hoursEditValue: {type: String, default: ""},
    /** When true, show reassign column whenever project list is loaded (admin). */
    showReassignUi: {type: Boolean, default: false},
  },
  computed: {
    showReassignColumn() {
      if (!this.projectOptions.length) return false;
      if (this.showReassignUi) return true;
      return (this.entries || []).some((e) => this.canReassignEntry(e));
    },
  },
  methods: {
    entryKey(entry, idx) {
      return entry.timesheetId
        ? `ts-${entry.timesheetId}`
        : `e-${idx}-${entry.dateLabel}`;
    },
    editedByTitle(entry) {
      if (!entry.editedAt) {
        return `Last edited from Koneko A by ${entry.editedByLabel}`;
      }
      try {
        const when = new Date(entry.editedAt).toLocaleString();
        return `Edited from Koneko A by ${entry.editedByLabel} · ${when}`;
      } catch {
        return `Edited from Koneko A by ${entry.editedByLabel}`;
      }
    },
    onHoursDblClick(entry) {
      if (!this.canEditHours(entry)) return;
      this.$emit("edit-hours", entry);
    },
    onProjectChange(entry, newProjectId) {
      if (!newProjectId || Number(newProjectId) === Number(entry.projectId)) {
        return;
      }
      const opt = this.projectOptions.find(
        (p) => Number(p.id) === Number(newProjectId)
      );
      this.$emit("reassign", entry, newProjectId, opt ? opt.name : "");
    },
  },
};
</script>

<style scoped>
.snap-entry-date-col {
  width: 6rem;
  white-space: nowrap;
}

.snap-entry-hours-col {
  min-width: 8rem;
  white-space: nowrap;
}

.snap-entry-hours-cell {
  vertical-align: middle;
}

.snap-hours-editable {
  cursor: pointer;
  border-bottom: 1px dashed rgba(0, 0, 0, 0.25);
}

.snap-hours-input {
  max-width: 5.5rem;
  font-size: 12px;
}

.snap-edited-by-chip {
  font-size: 10px !important;
  height: 18px !important;
}

.snap-entry-project-col {
  min-width: 180px;
  max-width: 240px;
}

.snap-entry-notes {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.35;
  max-width: 36rem;
}

.snap-reassign-select {
  font-size: 12px;
}
</style>
